import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosApiInstance from '../../../api/axiosClient';
import { PageContextProvider } from './DDEXMainPage';
import { Button, Table, Tag, Typography, Space, message, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import './ReleaseEntryStepForm.css';

const { Title, Text } = Typography;

// Define the BatchesTable component
const BatchesTable = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [batches, setBatches] = useState<API.MusicDistribution.ReleaseBatch[]>([]);
    const [error, setError] = useState<string | null>(null);

    // For backward compatibility with PageContextProvider
    const releasesActionRef = React.useRef<any>(null);
    const listRef = React.useRef<any>(null);
    const [artists, setArtists] = React.useState<any>({});

    // Fetch batches on component mount
    useEffect(() => {
        fetchBatches();
    }, []);

    // Function to fetch all batches
    const fetchBatches = async () => {
        try {
            setLoading(true);
            const response = await axiosApiInstance.get('/api/products/batch_releases/');
            if (response.status === 200) {
                setBatches(response.data);
            } else {
                throw new Error('Failed to fetch batches');
            }
        } catch (err) {
            console.error('Error fetching batches:', err);
            setError('Failed to fetch batches');
        } finally {
            setLoading(false);
        }
    };

    // Function to handle batch deletion
    const handleDeleteBatch = async (batchId: string) => {
        try {
            await axiosApiInstance.delete(`/api/products/batch_releases/${batchId}`);
            message.success('Batch deleted successfully');
            fetchBatches(); // Refresh the list
        } catch (error) {
            console.error('Failed to delete batch:', error);
            message.error('Failed to delete batch');
        }
    };

    // Function to get status tag color
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending':
                return 'orange';
            case 'processing':
                return 'blue';
            case 'completed':
                return 'green';
            case 'failed':
                return 'red';
            default:
                return 'default';
        }
    };

    // Table columns definition
    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: API.MusicDistribution.ReleaseBatch) => (
                <a onClick={() => navigate(`/music/batch/${record.id}`)}>{text}</a>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={getStatusColor(status)}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                </Tag>
            ),
        },
        {
            title: 'Releases',
            key: 'releases',
            render: (_, record: API.MusicDistribution.ReleaseBatch) => (
                <span>{record.batch_items?.length || 0}</span>
            ),
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date: string) => new Date(date).toLocaleString(),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record: API.MusicDistribution.ReleaseBatch) => (
                <Space size="middle">
                    <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => navigate(`/music/batch/${record.id}`)}
                    >
                        View
                    </Button>
                    <Button 
                        danger 
                        size="small" 
                        onClick={() => handleDeleteBatch(record.id)}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    if (loading) {
        return <Spin tip="Loading..." />;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <PageContextProvider
            value={{
                releasesActionRef,
                listRef,
                artists,
                setArtists
            }}
        >
            <div className="release-entry-container" style={{ width: '100%', maxWidth: 1600, margin: '0 auto' }}>
                <div className="release-entry-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                    <h2>Release Batches</h2>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate("/music/batch")}
                    >
                        Create New Batch
                    </Button>
                </div>

                <Table
                    dataSource={batches}
                    columns={columns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                />
            </div>
        </PageContextProvider>
    );
};

export default BatchesTable;